import { formatOrgNum } from "../../../utils/other";
import {
  BeneficialOwnersPortfolioStatus,
  CompanyState,
  concatenateShortcuts,
  formatARPortfolioDetails,
  formatBeneficialOwnersPortfolioDetails,
  PortfolioCompany,
  ShareholderRegisterStatementStatus,
} from "../../../stores/PortfolioStore";
import { capitalize, compact } from "lodash";
import { AccessType } from "../../../types/enums/AccessType";
import { Button, IconButton } from "../../../components-v2/Button";
import { Tag } from "../../../components-v2/Tag";
import { Icon } from "../../../components-v2/Icon";
import { Box, Card, Grid, GridItem, VBox } from "../../../components-v2/Box";
import { observer } from "mobx-react";
import { FC } from "react";
import { DataTable } from "../../../components-v2/DataTable";
import { DropdownMenu, DropdownMenuItemProps } from "../../../components-v2/Dropdown";
import {
  navigateToAdminDashboard,
  navigateToProjectOverview,
  navigateToShareholdersList,
  navigateToTransactions,
} from "../../../utils/navigation";
import { formatAmount } from "../../../utils/currency";
import { stopPropagation } from "../../../utils/events";
import { useWindowWidth } from "../../../utils/screen";
import { MeetingType } from "../../../meetings/meetingsDomain";
import { TFunction } from "../../../i18n/tt";
import { useLanguage, useRootStore, useTranslation } from "../../../stores/RootStoreContext";
import { PortfolioActions, PortfolioCompaniesTab } from "../../../portfolio/portfolioDomain";
import { formatPercent } from "../../../utils/numbers";
import { UserSettings } from "../../../types/UserInfo";
import { when } from "../../../utils/functions";
import { toDate } from "../../../utils/Dates";

export const CompanyTable: FC<{
  data: PortfolioCompany[];
  activeTab: PortfolioCompaniesTab;
  onSelect: (company: PortfolioCompany) => void;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
  portfolioActions: PortfolioActions;
  userSettings: UserSettings | null;
}> = observer(
  ({ data, activeTab, onSelect, onClickStar, isRenderStar, portfolioActions, userSettings }) => {
    const windowWidth = useWindowWidth();
    if (windowWidth === "sm") {
      return (
        <SmallScreenCompanyTable
          data={data}
          activeTab={activeTab}
          onSelect={onSelect}
          onClickStar={onClickStar}
          isRenderStar={isRenderStar}
        />
      );
    }
    return (
      <DesktopScreenCompanyTable
        data={data}
        activeTab={activeTab}
        onSelect={onSelect}
        onClickStar={onClickStar}
        isRenderStar={isRenderStar}
        isLargeScreen={windowWidth === "lg"}
        portfolioActions={portfolioActions}
        userSettings={userSettings}
      />
    );
  },
);

function renderUnconnectedCompanyColumn(c: PortfolioCompany, t: TFunction) {
  return c.state === CompanyState.DRAFT ? (
    <Box alignItems={"center"}>
      <Button
        variant={"secondary"}
        label={t("portfolio.askForPermissionButton")}
        size={"xs"}
        fullWidth
      />
      <span data-tooltip={t("portfolio.askForPermissionTooltipText")}>
        <Icon name={"info"} />
      </span>
    </Box>
  ) : c.accessType === AccessType.NONE ? (
    <Box alignItems={"center"}>
      <Button
        icon={"plugs"}
        label={t("portfolio.connectCompanyButton")}
        size={"xs"}
        variant={"secondary"}
        fullWidth
      />
    </Box>
  ) : c.state === CompanyState.PENDING ? (
    <Box className={"subtle text-sm sm:tw-justify-end"}>
      <span>{t("portfolio.pendingPermissionRequest")}</span>
      <span data-tooltip={t("portfolio.pendingPermissionTooltipText")}>
        <Icon name={"info"} />
      </span>
    </Box>
  ) : undefined;
}

function AccessTypeTag({ company }: { company: PortfolioCompany }) {
  const { t } = useTranslation();
  return company.accessType === AccessType.ADMIN ? (
    <Tag color="info" label={capitalize(company.accessType)} />
  ) : company.accessType === AccessType.SHAREHOLDER ? (
    <Tag color="success" label={t("portfolio.SHAREHOLDER")} />
  ) : company.accessType === AccessType.ADDITIONAL_REPRESENTATIVE ? (
    <Tag color="success" label={t("portfolio.ADDITIONAL_REPRESENTATIVE")} />
  ) : company.accessType === AccessType.BOARD_MEMBER ? (
    <Tag color="warning" label={t("portfolio.BOARD_MEMBER")} />
  ) : company.accessType === AccessType.SUPERADMIN ? (
    <Tag color="error" label={capitalize(company.accessType)} />
  ) : company.state === CompanyState.DRAFT ? (
    <Tag color="error" label={t("portfolio.askForPermissionDraftPill")} />
  ) : company.accessType === AccessType.CUSTOM && company.role ? (
    <Tag color="info" label={{ translated: company.role.customRoleName }} />
  ) : null;
}

export const DesktopScreenCompanyTable: FC<{
  data: PortfolioCompany[];
  activeTab: PortfolioCompaniesTab;
  onSelect: (company: PortfolioCompany) => void;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
  isLargeScreen: boolean;
  portfolioActions: PortfolioActions;
  userSettings: UserSettings | null;
}> = observer(
  ({
    data,
    activeTab,
    onSelect,
    onClickStar,
    isRenderStar,
    isLargeScreen,
    portfolioActions,
    userSettings,
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const isOnboardedCompanyTab =
      activeTab !== "not-connected" &&
      activeTab !== "beneficial-owners" &&
      activeTab !== "ar-status";
    return (
      <DataTable<PortfolioCompany>
        inline
        getId={(c) => `${c.organisationNumber}_${c.accessType}_${c.shareholderId}`}
        variant={"LARGE"}
        onRowClick={onSelect}
        tableData={data}
        initialSortBy={"lastVisited"}
        initialSortDirection={"desc"}
        tableColumns={compact([
          {
            align: "left",
            name: "star",
            label: "",
            width: "1%",
            notSorted: true,
            getValue: (c) =>
              c.isCustomer && (
                <StarButton company={c} onClickStar={onClickStar} isRenderStar={isRenderStar} />
              ),
          },
          {
            align: "left",
            name: "name",
            label: t("portfolio.table.header.company"),
            getSortValue: (c) => c.name.toLocaleLowerCase(),
            getValue: (c) => (
              <VBox>
                <div>{c.name}</div>
                {c.accessType === AccessType.SHAREHOLDER ? (
                  <div className={"subtle text-sm"}>{t("portfolio.my-investments")}</div>
                ) : c.accessType === AccessType.ADDITIONAL_REPRESENTATIVE ? (
                  <div className={"subtle text-sm"}>{t("portfolio.my-investments")}</div>
                ) : c.accessType === AccessType.BOARD_MEMBER ? (
                  <div className={"subtle text-sm"}>{t("portfolio.with-board-role")}</div>
                ) : c.portfolioName ? (
                  <div className={"subtle text-sm"}>{c.portfolioName}</div>
                ) : null}
                {(c.onBehalfOfShareholderCompany || c.onBehalfOfShareholderPerson) && (
                  <div className={"subtle text-sm"}>
                    {t("portfolio.onBehalfOf", {
                      name: c.onBehalfOfShareholderCompany ?? c.onBehalfOfShareholderPerson,
                    })}
                  </div>
                )}
              </VBox>
            ),
          },
          {
            align: "left",
            name: "organisationNumber",
            label: t("portfolio.table.header.organisation-number"),
            getValue: (c) => (
              <button
                className="hover:underline"
                data-tooltip={t("general.copy-to-clipboard")}
                onClick={stopPropagation(() =>
                  portfolioActions.copyToClipboard(c.organisationNumber),
                )}
              >
                {formatOrgNum(c.organisationNumber)}
              </button>
            ),
          },
          isOnboardedCompanyTab && {
            align: "left",
            name: "accessType",
            label: t("portfolio.table.header.access-type"),
            getValue: (c) => <AccessTypeTag company={c} />,
          },
          isLargeScreen &&
            isOnboardedCompanyTab &&
            data.some((c) => c.accessType === AccessType.ADMIN) && {
              align: "right",
              name: "shareCapital",
              label: t("portfolio.table.header.share-capital"),
              getValue: (c) =>
                c.accessType === AccessType.ADMIN &&
                c.shareCapital &&
                formatAmount(locale, c.currency, c.shareCapital),
            },
          isLargeScreen &&
            isOnboardedCompanyTab && {
              align: "right",
              name: "numberOfShareholders",
              label: t("portfolio.table.header.shareholders"),
              getValue: (c) => c.numberOfShareholders,
            },
          isLargeScreen &&
            activeTab === "not-connected" && {
              align: "left",
              name: "ownershipInPercent",
              label: t("ownership"),
              getValue: (c) => (
                <Box>
                  {c.sourceOrgNoName}{" "}
                  <div className={"subtle"}>{formatPercent(locale, c.ownershipInPercent)}</div>
                </Box>
              ),
            },
          activeTab === "ar-status" && {
            align: "left",
            name: "arStatus",
            label: t("status"),
            getSortValue: (c) => c.shareholderRegisterStatementStatus?.type,
            getValue: (c) => <ARStatusTag status={c.shareholderRegisterStatementStatus} />,
          },
          activeTab === "ar-status" && {
            align: "left",
            name: "arStatusDetails",
            label: t("details"),
            getSortValue: (c) =>
              t(formatARPortfolioDetails(locale, c.shareholderRegisterStatementStatus)),
            getValue: (c) => formatARPortfolioDetails(locale, c.shareholderRegisterStatementStatus),
          },
          activeTab === "beneficial-owners" && {
            align: "left",
            name: "beneficialOwnerPortfolioStatus",
            label: t("status"),
            getSortValue: (c) => c.beneficialOwnersPortfolioStatus?.type,
            getValue: (c) => (
              <BeneficialOwnersStatusTag status={c.beneficialOwnersPortfolioStatus} />
            ),
          },
          activeTab === "beneficial-owners" && {
            align: "left",
            name: "beneficialOwnerPortfolioDetails",
            label: t("details"),
            getSortValue: (c) =>
              t(formatBeneficialOwnersPortfolioDetails(locale, c.beneficialOwnersPortfolioStatus)),
            getValue: (c) =>
              formatBeneficialOwnersPortfolioDetails(locale, c.beneficialOwnersPortfolioStatus),
          },
          activeTab !== "not-connected" && {
            align: "left",
            name: "lastVisited",
            label: { key: "last-visited" },
            getSortValue: (c) =>
              when(userSettings?.lastVisitedCompanies?.[c.organisationNumber], (ts) =>
                toDate(ts).getTime(),
              ) ?? 0,
            getValue: (c) =>
              when(userSettings?.lastVisitedCompanies?.[c.organisationNumber], (ts) => ({
                type: "datetime",
                relative: true,
                value: ts,
              })),
          },
          {
            align: "right",
            name: "actions",
            label: "",
            notSorted: true,
            getValue: (c) =>
              c.accessType === AccessType.ADMIN ? (
                <CompanyNavigationMenu company={c} portfolioActions={portfolioActions} />
              ) : (
                <></>
              ),
          },
          !isOnboardedCompanyTab && {
            align: "right",
            name: "onboard",
            label: "",
            notSorted: true,
            getValue: (c) => renderUnconnectedCompanyColumn(c, t),
          },
        ])}
      />
    );
  },
);

function ARStatusTag({ status }: { status: ShareholderRegisterStatementStatus | undefined }) {
  switch (status?.type) {
    case "Sent":
      return <Tag label={{ key: "submitted" }} color={"success"} />;
    case "Signed":
      return <Tag label={{ key: "submitted" }} color={"success"} />;
    case "NotSent":
      return <Tag label={{ key: "not-submitted" }} color={"neutral"} />;
    default:
      return null;
  }
}

function BeneficialOwnersStatusTag({
  status,
}: {
  status: BeneficialOwnersPortfolioStatus | undefined;
}) {
  switch (status?.type) {
    case "Sent":
      return <Tag label={{ key: "submitted" }} color={"success"} />;
    case "NotSent":
      return <Tag label={{ key: "not-submitted" }} color={"neutral"} />;
    case "Overdue":
      return <Tag label={{ key: "overdue" }} color={"error"} />;
    default:
      return null;
  }
}

export const CompanyNavigationMenu: FC<{
  company: PortfolioCompany;
  portfolioActions: PortfolioActions;
}> = observer(({ company, portfolioActions }) => {
  const { t } = useTranslation();
  const history = useRootStore().history;
  const locale = useLanguage();

  const standardShortcuts: DropdownMenuItemProps[] = [
    {
      type: "item",
      label: t("portfolio.links.dashboard"),
      onSelect: () => navigateToAdminDashboard(company.organisationNumber, history),
    },
    {
      type: "item",
      label: t("portfolio.links.add-admin"),
      onSelect: () =>
        portfolioActions.onAddAdministrator({
          companyItem: {
            organisationNumber: company.organisationNumber,
            companyName: company.name,
          },
          lang: locale,
        }),
    },
    {
      type: "item",
      label: t("portfolio.links.edit-shareholder"),
      onSelect: () => navigateToShareholdersList(company.organisationNumber, history),
    },
    {
      type: "item",
      label: t("portfolio.links.new-transaction"),
      onSelect: () => navigateToTransactions(company.organisationNumber, history),
    },
  ];
  const advancedShortcuts: DropdownMenuItemProps[] = [
    {
      type: "item",
      label: t("portfolio.links.new-company-event"),
      onSelect: () => navigateToProjectOverview(company.organisationNumber, history),
    },
    {
      type: "item",
      label: t("portfolio.links.new-board-meeting"),
      onSelect: () =>
        portfolioActions.onNewMeeting(company.organisationNumber, MeetingType.BOARD_MEETING),
    },
    {
      type: "item",
      label: t("portfolio.links.new-general-meeting"),
      onSelect: () =>
        portfolioActions.onNewMeeting(company.organisationNumber, MeetingType.GENERAL_MEETING),
    },
  ];
  const portfolioInstanceShortcuts: DropdownMenuItemProps[] = compact([
    portfolioActions.onRemoveFromPortfolio && {
      type: "item",
      label: t("portfolio.links.remove-from-portfolio"),
      onSelect: () => portfolioActions.onRemoveFromPortfolio?.(company),
    },
    portfolioActions.onMoveToPortfolio && {
      type: "item",
      label: t("portfolio.links.move-to-portfolio"),
      onSelect: () => portfolioActions.onMoveToPortfolio?.(company),
    },
  ]);
  return (
    <DropdownMenu
      button={{
        type: "dotdotdot",
        tooltip: t("options"),
      }}
      items={concatenateShortcuts<DropdownMenuItemProps>(
        standardShortcuts,
        advancedShortcuts,
        portfolioInstanceShortcuts,
      )}
      alignment={"end"}
    />
  );
});

export const SmallScreenCompanyTable: FC<{
  data: PortfolioCompany[];
  activeTab: PortfolioCompaniesTab;
  onSelect: (company: PortfolioCompany) => void;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
}> = observer(({ data, activeTab, onSelect, onClickStar, isRenderStar }) => {
  const { t } = useTranslation();
  return (
    <Card padding={false}>
      <VBox gap={0}>
        {data.map((c) => {
          const hasAccess =
            c.accessType !== AccessType.NONE &&
            c.accessType !== AccessType.DRAFT &&
            c.state !== CompanyState.PENDING &&
            c.state !== CompanyState.DRAFT;
          return (
            <div
              className={"cursor-pointer"}
              key={c.id + c.organisationNumber + c.accessType + c.shareholderId}
              onClick={() => onSelect(c)}
            >
              <Grid className={"hover:bg-alt2"} grow alignItems={"center"} borderBottom padding>
                <GridItem xs={11} sm={4}>
                  <Box alignItems={"center"}>
                    {c.isCustomer && (
                      <StarButton
                        company={c}
                        onClickStar={onClickStar}
                        isRenderStar={isRenderStar}
                      />
                    )}

                    <div>
                      <div>{c.name}</div>
                      {(c.onBehalfOfShareholderCompany || c.onBehalfOfShareholderPerson) && (
                        <div className={"subtle text-sm"}>
                          {t("portfolio.onBehalfOf", {
                            name: c.onBehalfOfShareholderCompany ?? c.onBehalfOfShareholderPerson,
                          })}
                        </div>
                      )}
                    </div>
                  </Box>
                </GridItem>
                <GridItem
                  xs={hasAccess ? 11 : 12}
                  sm={2}
                  whitespace={"nowrap"}
                  overflowX={"hidden"}
                  className={"text-ellipsis subtle"}
                  textSize={"sm"}
                >
                  {formatOrgNum(c.organisationNumber)}
                </GridItem>
                <GridItem xs={hasAccess ? 11 : 12} sm={2} overflow={"hidden"}>
                  {activeTab === "beneficial-owners" ? (
                    <BeneficialOwnersStatusTag status={c.beneficialOwnersPortfolioStatus} />
                  ) : activeTab === "ar-status" ? (
                    <ARStatusTag status={c.shareholderRegisterStatementStatus} />
                  ) : (
                    <AccessTypeTag company={c} />
                  )}
                </GridItem>
                <GridItem xs={hasAccess ? 11 : 12} sm={hasAccess ? 3 : 4}>
                  {renderUnconnectedCompanyColumn(c, t)}
                </GridItem>
                {hasAccess && (
                  <GridItem
                    textAlign={"right"}
                    className={
                      "xs-only:tw-row-start-1 xs-only:tw-row-span-4 xs-only:tw-col-start-12"
                    }
                  >
                    {<Icon name={"caret-right"} />}
                  </GridItem>
                )}
              </Grid>
            </div>
          );
        })}
      </VBox>
    </Card>
  );
});

function StarButton({
  company,
  onClickStar,
  isRenderStar,
}: {
  company: PortfolioCompany;
  onClickStar: (organisationNumber: string, accessType: AccessType) => void;
  isRenderStar: (organisationNumber: string, accessType: AccessType) => boolean;
}) {
  const { t } = useTranslation();
  return (
    <IconButton
      tooltip={t("portfolio.star-tooltip")}
      color={"accent3"}
      icon={
        isRenderStar(company.organisationNumber, company.accessType)
          ? { name: "star", weight: "fill" }
          : { name: "star" }
      }
      onClick={stopPropagation(() => onClickStar(company.organisationNumber, company.accessType))}
    />
  );
}

/*
const shareholderRegisterStatementStatus: {
  [key in ShareholderRegisterStatementStatus]: TMessageKey;
} = {
  [ShareholderRegisterStatementStatus.NOT_SENT]: "shareholderRegisterStatementStatus.not-sent",
  [ShareholderRegisterStatementStatus.SENT]: "shareholderRegisterStatementStatus.sent",
  [ShareholderRegisterStatementStatus.SIGNED]: "shareholderRegisterStatementStatus.signed",
  [ShareholderRegisterStatementStatus.NOT_AVAILABLE]:
    "shareholderRegisterStatementStatus.not-available",
};
*/
